// 재심사 / 새로작성 선택 모달

import React, { useRef, useState } from "react";
import { styled, keyframes } from "styled-components";

import fonts from "@/design/typography";
import colors from "@/design/colors";
import IconX from "@/components/icons/x";
import Button from "@/components/button";
import { rootStore } from "@/mobx/store";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "@/components/common/ConfirmModal";

/* ----------------------------------
 * TYPES
 ---------------------------------- */
interface BusinessRegistrationRetryModalProps {
  onClose: () => void; // 모달 닫기
}

const BusinessRegistrationRetryModal: React.FC<
  BusinessRegistrationRetryModalProps
> = ({ onClose }) => {
  /* ----------------------------------
   * GLOBAL STATE
   ---------------------------------- */
  const navigate = useNavigate();

  /* ----------------------------------
   * LOCAL STATE
   ---------------------------------- */
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  // 모달 추적 ref
  const modalRef = useRef<HTMLDivElement>(null);

  /* ----------------------------------
   * Handlers
   ---------------------------------- */
  // 기존 등록 서류 수정하기
  const handleEditExisting = () => {
    // TODO: 기존 등록 서류 수정 페이지로 이동
    // 현재는 사업장 등록 페이지로 이동 (기존 등록 서류를 불러와서 수정)
    navigate("/business-registration/edit");
    onClose();
  };

  // 새로 작성하기
  const handleCreateNew = () => {
    // 새로 작성하기 위해 폼 초기화 후 사업장 등록 페이지로 이동
    rootStore.clearBusinessRegistrationForm();
    navigate("/business-registration");
    onClose();
  };

  // 외부 클릭 시 모달 닫기
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Overlay를 직접 클릭했을 때만 닫기 (모달 내부 클릭은 무시)
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 모달 내부 클릭 이벤트 전파 방지
  const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  /* ----------------------------------
   * EFFECTS
   * 컴포넌트 상태 변경 처리
   ---------------------------------- */

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalWrap ref={modalRef} onClick={handleModalClick}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Title></Title>
          <div onClick={onClose}>
            <HoverableIcon>
              <IconX color="#333" width="24px" height="24px" />
            </HoverableIcon>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Title>
            재심사 요청 시 반려 사유가 된 정보를 <br />
            다시 수정하거나 새로 작성할 수 있습니다
          </Title>

          <ButtonWrap>
            <Button
              size="medium"
              text="기존 등록 서류 수정하기"
              width="100%"
              height="56px"
              buttonColor={colors.gray100}
              textColor={colors.gray500}
              buttonHoverColor={colors.blue600}
              textHoverColor={colors.white}
              onClick={handleEditExisting}
            />
            <Button
              size="medium"
              text="새로 작성하기"
              width="100%"
              height="56px"
              buttonColor={colors.gray100}
              textColor={colors.gray500}
              buttonHoverColor={colors.blue600}
              textHoverColor={colors.white}
              onClick={() => setIsOpenConfirmModal(true)}
            />
          </ButtonWrap>

          <Info>
            <ul
              style={{
                margin: 0,
                paddingLeft: 0,
                listStyleType: "disc",
                listStyle: "none",
              }}
            >
              <li
                style={{
                  position: "relative",
                  paddingLeft: 16,
                  ...fonts.label5Medium,
                  fontWeight: 500,
                  fontSize: "13px",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    left: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 6,
                    height: 6,
                    background: colors.gray600,
                    borderRadius: "50%",
                    display: "inline-block",
                  }}
                />
                사업자 유형 및 카드 단말기 보유 여부는 기존 등록 서류에서는
                수정이 불가합니다. 수정을 희망하시는 경우 새로 작성해 주세요.
              </li>
            </ul>
          </Info>
        </div>
      </ModalWrap>

      {isOpenConfirmModal && (
        <ConfirmModal
          isOpen={isOpenConfirmModal}
          onClose={() => setIsOpenConfirmModal(false)}
          message={
            <>
              사업장 정보를 새로 작성하시겠습니까?
              <br />
              기존 작성 내용은 모두 초기화됩니다
            </>
          }
          buttons={[
            {
              text: "취소",
              onClick: () => setIsOpenConfirmModal(false),
              buttonColor: colors.gray100,
              textColor: colors.gray500,
            },
            {
              text: "확인",
              onClick: handleCreateNew,
              buttonColor: colors.blue600,
              textColor: colors.white,
            },
          ]}
        />
      )}
    </Overlay>
  );
};

/* ----------------------------------
 * Styles
 ---------------------------------- */
const fadeInOnly = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Overlay = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 9999;
  animation: ${fadeInOnly} 0.15s ease-in-out;
  // backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ModalWrap = styled.div`
  width: 480px;
  height: fit-content;
  border-radius: 20px;
  background-color: ${colors.white};
  position: relative;
  padding: 24px;
  overflow: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const Title = styled.div`
  font-size: ${fonts.title3SemiBold.fontSize};
  line-height: ${fonts.title3SemiBold.lineHeight};
  font-family: ${fonts.title3SemiBold.fontFamily};
  font-weight: 600;
  text-align: center;
  color: ${colors.gray900};
  margin-bottom: 40px;
`;

const HoverableIcon = styled.div`
  display: inline-flex;
  cursor: pointer;
`;

const ButtonWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-bottom: 40px;
`;

const Info = styled.div`
  font-size: ${fonts.label4Medium.fontSize};
  line-height: ${fonts.label4Medium.lineHeight};
  font-family: ${fonts.label4Medium.fontFamily};
  color: ${colors.gray600};
  background-color: ${colors.gray100};
  padding: 8px 16px;
  border-radius: 8px;
  width: 100%;
`;

export default BusinessRegistrationRetryModal;
